import { Component, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgbModal, NgbModalRef, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';

import { IdleTimeoutService } from '../../services/idle-timeout.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-idle-warning-modal',
  standalone: true,
  imports: [CommonModule, NgbModule],
  templateUrl: './idle-warning-modal.html',
  styleUrls: ['./idle-warning-modal.scss']
})
export class IdleWarningModalComponent implements OnInit, OnDestroy {
  private modalService = inject(NgbModal);
  private idleTimeoutService = inject(IdleTimeoutService);
  private authService = inject(AuthService);
  private router = inject(Router);
  
  @ViewChild('idleWarningModal') modalContent: any;
  
  private destroy$ = new Subject<void>();
  private modalRef: NgbModalRef | null = null;
  
  remainingSeconds: number = 0;
  
  // Modal configuration
  modalConfig = {
    title: 'Session Timeout Warning',
    size: 'sm' as const,
    centered: true,
    backdrop: 'static' as const,
    keyboard: false,
    closeButton: false
  };

  footerButtons = [
    {
      label: 'Logout',
      cssClass: 'btn-danger',
      action: () => this.onLogout()
    },
    {
      label: 'Stay Logged In',
      cssClass: 'btn-primary',
      action: () => this.onStayLoggedIn()
    }
  ];

  ngOnInit(): void {
    // Listen for idle warning events
    this.idleTimeoutService.getWarningObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(seconds => {
        this.remainingSeconds = seconds;
        
        // Open modal on first warning
        if (!this.modalRef) {
          this.openModal();
        }
      });

    // Listen for idle timeout events (auto-logout)
    this.idleTimeoutService.getTimeoutObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeModal();
        this.performLogout();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.closeModal();
  }

  /**
   * Open the warning modal
   */
  openModal(): void {
    if (this.modalRef) {
      return; // Modal already open
    }

    this.modalRef = this.modalService.open(this.modalContent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    // Handle modal dismissal
    this.modalRef.result.then(
      () => {
        this.modalRef = null;
      },
      () => {
        this.modalRef = null;
      }
    );
  }

  /**
   * Close the modal
   */
  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.dismiss();
      this.modalRef = null;
    }
  }

  /**
   * Handle logout button click
   */
  onLogout(): void {
    this.closeModal();
    this.performLogout();
  }

  /**
   * Handle stay logged in button click
   */
  onStayLoggedIn(): void {
    this.closeModal();
    this.idleTimeoutService.resetTimers();
  }

  /**
   * Perform logout action
   */
  private performLogout(): void {
    this.authService.logout().subscribe({
      next: () => {
        this.router.navigate(['/login']);
      },
      error: (error) => {
        console.error('Logout error:', error);
        // Navigate to login anyway
        this.router.navigate(['/login']);
      }
    });
  }

  /**
   * Format seconds to MM:SS
   */
  formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
